import { zodResolver } from '@hookform/resolvers/zod';
import { Label } from '@savvy-kit/components/label';
import { Textarea } from '@savvy-kit/components/textarea';
import InputField from '@savvy-kit/patterns/input-field';
import { isNil } from 'es-toolkit';
import { Controller, useForm } from 'react-hook-form';
import { formatCurrencyForSubmit } from 'src/utils/currency';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { ASSET_CLASS_REAL_ESTATE } from '@savvy/constants/asset-classes';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { AssetFormValues } from './add-account-schemas';
import {
  ADD_ACCOUNT_SAVE_ERROR,
  ASSET_FORM_TITLES,
  MANUAL_ASSET_CLASS_OPTIONS,
  NOTES_DESCRIPTION,
  NOTES_PLACEHOLDER,
  REAL_ESTATE_SUBCLASS_OPTIONS,
  resolveAssetClass,
} from './add-account-constants';
import { AddAccountFormShell } from './add-account-form-shell';
import { CLIENT_PORTAL_CREATE_ASSET } from './add-account-graphql';
import { AddAccountNumericField } from './add-account-numeric-field';
import { assetFormSchema } from './add-account-schemas';
import { AddAccountSelectField } from './add-account-select-field';

type AddAccountAssetFormProps = {
  readonly realEstate: boolean;
  readonly onCancel: () => void;
  readonly onCreated: (assetId: string) => Promise<unknown>;
  readonly submitLabel: string;
};

export function AddAccountAssetForm({ realEstate, onCancel, onCreated, submitLabel }: AddAccountAssetFormProps) {
  const [createAsset, { loading }] = useMutationWithErrorLogging(CLIENT_PORTAL_CREATE_ASSET);
  const { control, handleSubmit, formState } = useForm<AssetFormValues>({
    resolver: zodResolver(assetFormSchema),
    defaultValues: {
      description: '',
      assetClass: realEstate ? ASSET_CLASS_REAL_ESTATE : '',
      assetSubclass: '',
      currentValue: '',
      notes: '',
    },
  });

  const submit = handleSubmit(async (values) => {
    const assetClass = resolveAssetClass(values.assetClass);
    if (assetClass === undefined) {
      return;
    }
    const assetSubclass = REAL_ESTATE_SUBCLASS_OPTIONS.find((option) => option.value === values.assetSubclass);
    try {
      const result = await createAsset({
        variables: {
          input: {
            assetClass,
            assetSubclass: assetSubclass?.value,
            currencyCode: 'USD',
            currentValue: formatCurrencyForSubmit(values.currentValue) ?? '',
            description: values.description,
            notes: values.notes === '' ? undefined : values.notes,
          },
        },
      });
      const assetId = result.data?.createAsset?.asset.id;
      if (isNil(assetId) || assetId === '') {
        handleMutationError(new Error('createAsset resolved without an id'), ADD_ACCOUNT_SAVE_ERROR);
        return;
      }
      await onCreated(assetId);
    } catch (error) {
      handleMutationError(error, ADD_ACCOUNT_SAVE_ERROR);
    }
  });

  return (
    <AddAccountFormShell
      title={realEstate ? ASSET_FORM_TITLES.realEstate : ASSET_FORM_TITLES.other}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel={submitLabel}
      submitting={loading}
    >
      <Controller
        control={control}
        name="description"
        render={({ field }) => (
          <InputField
            {...field}
            id="add-asset-description"
            label={realEstate ? 'Address' : 'Name'}
            required
            autoFocus
            error={formState.errors.description !== undefined}
            errorMessage={formState.errors.description?.message}
          />
        )}
      />
      {!realEstate && (
        <Controller
          control={control}
          name="assetClass"
          render={({ field }) => (
            <AddAccountSelectField
              id="add-asset-class"
              label="Type"
              value={field.value}
              options={MANUAL_ASSET_CLASS_OPTIONS}
              onValueChange={field.onChange}
              errorMessage={formState.errors.assetClass?.message}
            />
          )}
        />
      )}
      {realEstate && (
        <Controller
          control={control}
          name="assetSubclass"
          render={({ field }) => (
            <AddAccountSelectField
              id="add-asset-subclass"
              label="Type"
              value={field.value}
              options={REAL_ESTATE_SUBCLASS_OPTIONS}
              onValueChange={field.onChange}
              errorMessage={formState.errors.assetSubclass?.message}
            />
          )}
        />
      )}
      <Controller
        control={control}
        name="currentValue"
        render={({ field }) => (
          <AddAccountNumericField
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            maxDecimals={2}
            id="add-asset-value"
            label="Value"
            required
            prependInputAddon="$"
            errorMessage={formState.errors.currentValue?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="notes"
        render={({ field }) => (
          <div>
            <Label htmlFor="add-asset-notes" className="pb-2">
              Notes
            </Label>
            <Textarea
              {...field}
              id="add-asset-notes"
              rows={3}
              placeholder={NOTES_PLACEHOLDER}
              aria-describedby="add-asset-notes-description"
            />
            <p id="add-asset-notes-description" className="text-secondary pt-1 text-xs">
              {formState.errors.notes?.message ?? NOTES_DESCRIPTION}
            </p>
          </div>
        )}
      />
    </AddAccountFormShell>
  );
}
